import RetailPartnerStoreDetailModal from "component/retailPartnerDetailModal/RetailPartnerStoreDetailModal";
import { SupplyMonitorMerchantPartnerResp } from "domain/payload/response/clientTransmora/storeDirectorySupplyMonitor/SupplyMonitorResp";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import StoreDirectorySupplyMonitorService from "service/ClientTransmoraService/StoreDirectorySupplyMonitorService";

interface AreaMerchantPartnerListModalProps {
  show: boolean;
  areaIds: string[];
  representativeAreaId: string;
  onClose: () => void;
}

const AreaMerchantPartnerListModal: React.FC<AreaMerchantPartnerListModalProps> = ({
  show,
  areaIds,
  representativeAreaId,
  onClose,
}) => {
  const { profile } = useSelector((state: any) => state.auth);
  const supplyMonitorService = StoreDirectorySupplyMonitorService();

  const [data, setData] = useState<SupplyMonitorMerchantPartnerResp[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Nested modal: detail toko
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);

  const fetchData = async () => {
    const token = profile?.data?.token;
    if (!token || areaIds.length === 0) return;

    setLoading(true);
    try {
      const res = await supplyMonitorService.SupplyMonitorAreaMerchantPartnerList(
        token, searchTerm, currentPage, pageSize, areaIds
      );
      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const [hierarchy, setHierarchy] = useState("");

  useEffect(() => {
      if (!show || !representativeAreaId) return;

      const fetchHierarchy = async () => {
          try {
              const res = await supplyMonitorService.SupplyMonitorAreaHierarchy(
                  profile.data.token,
                  representativeAreaId  // ini sekarang areaIds[0], bukan areaId
              );
              if (res.data) {
                  setHierarchy(res.data.hierarchy);
              }
          } catch (err) {
              console.error("Failed to fetch hierarchy:", err);
          }
      };

      fetchHierarchy();
  }, [show, representativeAreaId]);

  useEffect(() => {
    if (show && areaIds.length > 0) {
      fetchData();
    }
  }, [show, areaIds, currentPage, pageSize]);

  useEffect(() => {
    if (!show) {
      setData([]);
      setSearchTerm("");
      setCurrentPage(1);
      setHierarchy("");
    }
  }, [show]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData();
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        onClick={onClose}
      >
        <div
          className="modal-dialog modal-lg modal-dialog-scrollable"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content position-relative">
            {loading && (
              <div
                className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
                style={{ backgroundColor: "rgba(255,255,255,0.8)", zIndex: 9999 }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            <div className="modal-header">
              <h5 className="modal-title fw-bold">Merchant Partner List</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={loading}></button>
            </div>

            <div className="modal-body">
              {hierarchy && (
                <div className="d-flex align-items-center gap-1 text-muted mb-3" style={{ fontSize: 13 }}>
                  <i className="fa-solid fa-location-dot text-danger" style={{ fontSize: 11 }}></i>
                  <div className="fw-semibold">{hierarchy}</div>
                </div>
              )}

              <form className="d-flex mb-3" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button type="submit" className="btn btn-primary px-4">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <table className="table table-condensed table-hover">
                <thead className="table-primary">
                  <tr>
                    <th>ID</th>
                    <th>Store Name</th>
                    <th>Owner</th>
                    <th>Address</th>
                    <th>Reference</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {!loading ? (
                    data.length > 0 ? (
                      data.map((mp) => (
                        <tr key={mp.id}>
                          <td>{mp.id}</td>
                          <td>{mp.name}</td>
                          <td>{mp.ownerName || "-"}</td>
                          <td>{mp.address || "-"}</td>
                          <td>{mp.reference}</td>
                          <td>
                            <button
                              className="btn btn-outline-info btn-small-compact border-0"
                              onClick={() => setSelectedStoreId(mp.id)}
                            >
                              <i className="fa-solid fa-eye"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center">Tidak ada data</td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="d-flex justify-content-end align-items-center mt-3">
                <div className="d-flex align-items-center">
                  <span className="me-3">
                    {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                  </span>
                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                          <i className="fa fa-angle-left"></i>
                        </button>
                      </li>
                      <li className="page-item disabled">
                        <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                      </li>
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                          <i className="fa fa-angle-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>

            <div className="modal-footer border-top">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={loading}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Nested modal — detail toko, z-index lebih tinggi */}
      {selectedStoreId && (
        <RetailPartnerStoreDetailModal
          show={!!selectedStoreId}
          storeId={selectedStoreId}
          onClose={() => setSelectedStoreId(null)}
        />
      )}
    </>
  );
};

export default AreaMerchantPartnerListModal;